import { DomainError } from './errors.ts';
import type { StudyState } from './lifecycle.ts';
import {
  CUSTOM_STUDY_TYPE_ID,
  categoryIdSchema,
  currentTemplateVersion,
  isCategoryId,
  isStudyTypeId,
  studyTypeById,
  studyTypeIdSchema,
  templateFor,
  type StudyTypeTemplate,
} from './study-types.ts';
import { z } from 'zod';

/**
 * What a study records about its Study Type: which type, which Category context, and which
 * version of that type's template it is pinned to.
 *
 * The three travel together because none of them means anything alone — a type with no version
 * resolves no content, and a version with no type is a version of nothing.
 */
export const studyTypeSelectionSchema = z
  .object({
    id: studyTypeIdSchema,
    /**
     * The Category a Custom study was started from, and null for a named type, whose Category the
     * catalogue is the authority on.
     */
    category: categoryIdSchema.nullable(),
    templateVersion: z.int().positive(),
  })
  .readonly();
export type StudyTypeSelection = z.infer<typeof studyTypeSelectionSchema>;

/**
 * The choice a researcher just made, turned into what the study stores.
 *
 * The version is resolved here rather than taken from the caller, because picking a different type
 * is the one moment a study's template version may move: a pick resolves to the current version of
 * the type that was picked, and nothing else ever writes the column. Picking the type the study
 * already has keeps the version it stored (`studyTypeAfterPick`), so a study pinned to an older
 * template is never moved onto a newer one by choosing the same card again.
 *
 * A named type's Category comes from the catalogue, so the column stays null for one and a caller
 * that names a Category the catalogue disagrees with is refused rather than quietly corrected: the
 * two would have been drawn from the same table, so a disagreement means the caller is working
 * from a catalogue this build does not have.
 *
 * Custom's Category is optional, and a Custom study with none is an ordinary study rather than an
 * incomplete one. It records where the reader was standing when they said none of these fitted, so
 * a reader who never stood anywhere — who described the study in a sentence instead — has none to
 * record, and inventing one would be putting words in their mouth.
 */
export const resolveStudyTypeSelection = ({
  studyTypeId,
  categoryId,
}: {
  studyTypeId: string;
  categoryId?: string | null;
}): StudyTypeSelection => {
  // The narrowing the three lookups below need, and a refusal that duplicates theirs: an id the
  // catalogue does not hold has no current template version either, so removing this guard changes
  // no answer — it changes which of the two refusals a caller gets there first.
  if (!isStudyTypeId(studyTypeId)) {
    throw new DomainError('study_type_unknown', `no study type ${studyTypeId}`, {
      study_type_id: studyTypeId,
    });
  }

  if (categoryId !== undefined && categoryId !== null && !isCategoryId(categoryId)) {
    throw new DomainError('study_type_unknown', `no category ${categoryId}`, {
      category_id: categoryId,
    });
  }

  const version = currentTemplateVersion(studyTypeId);
  if (version === undefined) {
    throw new DomainError(
      'study_type_unknown',
      `the template registry holds nothing for ${studyTypeId}`,
      { study_type_id: studyTypeId },
    );
  }

  if (studyTypeId === CUSTOM_STUDY_TYPE_ID) {
    return { id: studyTypeId, category: categoryId ?? null, templateVersion: version };
  }

  const catalogued = studyTypeById(studyTypeId)?.categoryId;
  if (categoryId !== undefined && categoryId !== null && categoryId !== catalogued) {
    throw new DomainError(
      'study_type_unknown',
      `${studyTypeId} is not in the ${categoryId} category`,
      { study_type_id: studyTypeId, category_id: categoryId },
    );
  }

  return { id: studyTypeId, category: null, templateVersion: version };
};

/**
 * The Study Type a study holds after a pick: the study's own when the pick names the type and
 * Category it already has, so the template version it stored stays, and the pick otherwise.
 *
 * The same choice again is no change. Answering it with the current version instead would move a
 * study whose Materials were confirmed on an older template onto a newer one, which un-confirms them
 * and can drop a field the newer template no longer has, all for pressing the card already chosen.
 */
export const studyTypeAfterPick = (
  current: StudyTypeSelection | null,
  picked: StudyTypeSelection,
): StudyTypeSelection =>
  current !== null && current.id === picked.id && current.category === picked.category
    ? current
    : picked;

/**
 * What the three stored columns mean, or null for a study that has chosen no type.
 *
 * A stored id and version reach the catalogue by assertion rather than through `isStudyTypeId`, so
 * both a type the catalogue stopped holding and a version the registry no longer carries are
 * reachable here in a way the types cannot see. Either is refused loudly rather than resolved to
 * the newest template, because falling back is exactly the silent change the version exists to
 * prevent — so retiring a template version a study has stored is a data decision, not a code one.
 */
export const parseStoredStudyType = (stored: {
  id?: string | null;
  category?: string | null;
  templateVersion?: number | null;
}): StudyTypeSelection | null => {
  const id = stored.id ?? null;
  const templateVersion = stored.templateVersion ?? null;
  if (id === null && templateVersion === null) {
    return null;
  }

  const parsed = studyTypeSelectionSchema.safeParse({
    id,
    category: stored.category ?? null,
    templateVersion,
  });
  if (!parsed.success || templateFor(parsed.data.id, parsed.data.templateVersion) === undefined) {
    throw new DomainError(
      'study_type_version_missing',
      `this build holds no version ${String(templateVersion)} of ${String(id)}`,
      { study_type_id: id, template_version: templateVersion },
    );
  }

  return parsed.data;
};

/** The template a stored selection resolves to, which is never the newest by accident. */
export const templateForSelection = (selection: StudyTypeSelection): StudyTypeTemplate => {
  const template = templateFor(selection.id, selection.templateVersion);
  if (template === undefined) {
    throw new DomainError(
      'study_type_version_missing',
      `this build holds no version ${String(selection.templateVersion)} of ${selection.id}`,
      { study_type_id: selection.id, template_version: selection.templateVersion },
    );
  }

  return template;
};

export interface StudyTypeEditability {
  state: StudyState;
  lockedAt: Date | null;
  launchedAt: Date | null;
  paidAt: Date | null;
}

/**
 * Whether the Study Type may still change.
 *
 * The three timestamps rather than the state column alone, for the reason `isStudyDeletable`
 * gives: they are the columns the claim rests on, and a study carrying any of them has had its
 * shape paid for, frozen or fielded. The state is checked as well so a study that reached a
 * terminal state by some other route is not offered a change either.
 *
 * What a changed type does to work already done downstream is a separate question, owned by the
 * step that lets earlier choices be revisited.
 */
export const isStudyTypeEditable = ({
  state,
  lockedAt,
  launchedAt,
  paidAt,
}: StudyTypeEditability): boolean =>
  lockedAt === null &&
  launchedAt === null &&
  paidAt === null &&
  (state === 'draft' || state === 'ready');

export const assertStudyTypeEditable = (study: StudyTypeEditability): void => {
  if (!isStudyTypeEditable(study)) {
    throw new DomainError(
      'study_type_not_editable',
      'a study that has been paid for, frozen or launched keeps the type it was run as',
      {
        state: study.state,
        locked_at: study.lockedAt?.toISOString() ?? null,
        launched_at: study.launchedAt?.toISOString() ?? null,
        paid_at: study.paidAt?.toISOString() ?? null,
      },
    );
  }
};
